import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  X,
  BookOpen,
  Clock,
  Pencil,
  Trash2,
  Lock,
  Unlock,
  ListOrdered,
  FileText,
  Loader2,
  GraduationCap,
  MapPin,
  Hash,
  User,
  CalendarDays,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { hasPermission } from "../../../utils/permissionUtils";
import { showConfirmDialog, showErrorToast } from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { getCourseById } from "../../../api/elearning/courses/courses";
import { getAllLessons } from "../../../api/elearning/lessons/lessons";
import { getLevelOptions } from "../../../utils/dropdownOptions";

const levelLabelMap = Object.fromEntries(
  getLevelOptions().map((o) => [o.value, o.label])
);

const formatLevels = (levelStr) => {
  if (!levelStr) return "—";
  return String(levelStr)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .map((v) => levelLabelMap[v] || v)
    .join(", ");
};

const CourseDetailModal = ({
  courseId,
  onClose,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lessonsLoading, setLessonsLoading] = useState(false);

  const loadLessons = useCallback(async (id) => {
    if (!id) {
      setLessons([]);
      return;
    }
    setLessonsLoading(true);
    try {
      const data = await getAllLessons({
        course_id: id,
        page: 1,
        size: 200,
      });
      const records = Array.isArray(data?.records) ? data.records : [];
      setLessons(
        [...records].sort(
          (a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)
        )
      );
    } catch (err) {
      console.error("Không tải được danh sách bài học:", err);
      setLessons([]);
    } finally {
      setLessonsLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) return;

      try {
        setLoading(true);
        const data = await getCourseById(courseId);
        setCourse(data);
        loadLessons(courseId);
      } catch (error) {
        console.error("Lỗi khi tải chi tiết khóa học:", error);
        showErrorToast(error?.message || "Không thể tải chi tiết khóa học");
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, loadLessons]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!courseId || typeof document === "undefined") return null;

  const isActive = course?.is_active === true || course?.status === "active";
  const lessonCount = lessons.length || Number(course?.lesson_count) || 0;

  const handleDelete = async () => {
    if (!course) return;
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.course,
      name: course.name,
    });
    if (confirm.isConfirmed) onDelete(course.id);
  };

  const handleToggleStatus = async () => {
    if (!course) return;
    const confirm = await showConfirmDialog({
      title: isActive ? "Ngưng khóa học" : "Kích hoạt khóa học",
      text: isActive
        ? `Ngưng sử dụng khóa học "${course.name}"?`
        : `Kích hoạt lại khóa học "${course.name}"?`,
      confirmText: isActive ? "Ngưng" : "Kích hoạt",
    });
    if (confirm.isConfirmed) onToggleStatus(course.id, !isActive);
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-[2px]"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-slate-50 rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-white"
          initial={{ scale: 0.94, opacity: 0, y: 24 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.94, opacity: 0, y: 16 }}
          transition={{ type: "spring", stiffness: 280, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}
          <div className="bg-white px-6 py-5 flex justify-between items-start border-b border-gray-100 shrink-0">
            <div className="flex items-start gap-4 min-w-0">
              <div className="w-12 h-12 shrink-0 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-200">
                <BookOpen className="text-white w-6 h-6" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900">
                    Chi tiết khóa học
                  </h2>
                  {course && (
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide ${
                        isActive
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                          : "bg-gray-100 text-gray-500 border border-gray-200"
                      }`}
                    >
                      {isActive ? "Đang dùng" : "Ngưng"}
                    </span>
                  )}
                </div>
                {course ? (
                  <>
                    <p className="mt-1 text-base font-extrabold text-gray-800 truncate">
                      {course.name}
                    </p>
                    {course.code ? (
                      <p className="mt-0.5 font-mono text-xs text-gray-400">
                        {course.code}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="mt-1 text-sm text-gray-400">Đang tải...</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
            >
              <X size={22} />
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5 min-h-0">
            {loading || !course ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin text-red-600" />
                Đang tải chi tiết khóa học...
              </div>
            ) : (
              <>
                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="rounded-2xl border border-red-100 bg-white p-4 flex items-center gap-3">
                    <div className="rounded-xl bg-red-50 p-2.5 text-red-600">
                      <Clock size={18} />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        Thời lượng
                      </p>
                      <p className="text-sm font-bold text-gray-800">
                        {course.duration_hours ?? 0} giờ
                      </p>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-red-100 bg-white p-4 flex items-center gap-3">
                    <div className="rounded-xl bg-red-50 p-2.5 text-red-600">
                      <ListOrdered size={18} />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        Bài học
                      </p>
                      <p className="text-sm font-bold text-gray-800">
                        {lessonCount} bài
                      </p>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-red-100 bg-white p-4 flex items-center gap-3 col-span-2 sm:col-span-1">
                    <div className="rounded-xl bg-red-50 p-2.5 text-red-600">
                      <Hash size={18} />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        Thứ tự
                      </p>
                      <p className="text-sm font-bold text-gray-800">
                        {course.sort_order ?? 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Meta */}
                <div className="rounded-2xl border border-gray-100 bg-white p-5 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <MetaRow
                      icon={GraduationCap}
                      label="Môn học"
                      value={course.subject_name || "—"}
                    />
                    <MetaRow
                      icon={BookOpen}
                      label="Trình độ"
                      value={formatLevels(course.level)}
                    />
                    <MetaRow
                      icon={MapPin}
                      label="Đối tượng"
                      value={
                        <div className="flex flex-wrap gap-1.5">
                          {course.is_school && (
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-sky-50 text-sky-700 border border-sky-100 uppercase">
                              Trường học
                            </span>
                          )}
                          {course.is_center && (
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-violet-50 text-violet-700 border border-violet-100 uppercase">
                              Trung tâm
                            </span>
                          )}
                          {!course.is_school && !course.is_center && (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      }
                    />
                    <MetaRow
                      icon={User}
                      label="Người tạo"
                      value={course.created_by_name || "Hệ thống"}
                    />
                  </div>

                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                      Mô tả
                    </p>
                    {course.description ? (
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                        {course.description}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        Chưa có mô tả cho khóa học này
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 pt-2 border-t border-gray-50 text-[11px] text-gray-400">
                    <CalendarDays size={12} />
                    Cập nhật cuối:{" "}
                    <span className="font-semibold text-gray-500">
                      {course.last_changed
                        ? new Date(course.last_changed).toLocaleDateString("vi-VN")
                        : "—"}
                    </span>
                  </div>
                </div>

                {/* Lessons */}
                <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-50 rounded-lg">
                        <FileText className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
                          Danh sách bài học
                        </h3>
                        <p className="text-xs text-gray-400">
                          {lessonCount} bài trong khóa học này
                        </p>
                      </div>
                    </div>
                  </div>

                  {lessonsLoading ? (
                    <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Đang tải bài học...
                    </div>
                  ) : lessons.length === 0 ? (
                    <div className="px-5 py-10 text-center">
                      <p className="text-sm font-medium text-gray-500">
                        Chưa có bài học nào
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        Vào Sửa khóa học để nhập danh sách bài học từ Excel/CSV.
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="sticky top-0 bg-gray-50 text-[10px] uppercase tracking-wide text-gray-400">
                          <tr>
                            <th className="px-4 py-2.5 font-bold w-14">STT</th>
                            <th className="px-4 py-2.5 font-bold">Tên bài học</th>
                            <th className="px-4 py-2.5 font-bold w-28 text-right">
                              Tài liệu
                            </th>
                            <th className="px-4 py-2.5 font-bold w-24 text-right">
                              Trạng thái
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {lessons.map((lesson) => {
                            const active =
                              lesson.is_active === true ||
                              lesson.is_active === "true" ||
                              lesson.is_active == null;
                            return (
                              <tr
                                key={lesson.id}
                                className="hover:bg-red-50/30 transition-colors"
                              >
                                <td className="px-4 py-3 tabular-nums text-gray-500">
                                  {lesson.order ?? "—"}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="font-semibold text-gray-800">
                                    {lesson.title}
                                  </div>
                                  {lesson.description ? (
                                    <div className="mt-0.5 line-clamp-1 text-xs text-gray-400">
                                      {lesson.description}
                                    </div>
                                  ) : null}
                                </td>
                                <td className="px-4 py-3 text-right text-xs font-medium">
                                  {Number(lesson.document_count) > 0 ? (
                                    <span className="text-gray-600">
                                      {lesson.document_count} file
                                    </span>
                                  ) : (
                                    <span className="text-amber-600">Chưa có</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <span
                                    className={`inline-flex text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                      active
                                        ? "bg-emerald-50 text-emerald-700"
                                        : "bg-gray-100 text-gray-500"
                                    }`}
                                  >
                                    {active ? "Active" : "Off"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* FOOTER */}
          {course && (
            <div className="bg-white px-6 py-4 flex flex-wrap justify-end gap-2 border-t border-gray-100 shrink-0">
              {hasPermission("elearning.courses", "U") && (
                <button
                  type="button"
                  onClick={() => onEdit(course)}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition"
                >
                  <Pencil size={15} />
                  Sửa
                </button>
              )}

              {hasPermission("elearning.courses", "U") && (
                <button
                  type="button"
                  onClick={handleToggleStatus}
                  className={`px-4 py-2.5 rounded-xl border text-sm font-semibold flex items-center gap-2 transition ${
                    isActive
                      ? "border-amber-200 text-amber-700 hover:bg-amber-50"
                      : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  }`}
                >
                  {isActive ? <Lock size={15} /> : <Unlock size={15} />}
                  {isActive ? "Ngưng" : "Kích hoạt"}
                </button>
              )}

              {hasPermission("elearning.courses", "D") && lessonCount === 0 && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2.5 rounded-xl border border-red-200 text-sm font-semibold text-red-600 hover:bg-red-50 flex items-center gap-2 transition"
                >
                  <Trash2 size={15} />
                  Xóa
                </button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

const MetaRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 min-w-0">
    <div className="mt-0.5 rounded-lg bg-gray-50 p-1.5 text-gray-400 shrink-0">
      <Icon size={14} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
        {label}
      </p>
      <div className="mt-0.5 text-sm font-semibold text-gray-800 break-words">
        {value}
      </div>
    </div>
  </div>
);

export default CourseDetailModal;
